import NextLink from 'next/link';
import React, { forwardRef, HTMLAttributeAnchorTarget } from 'react';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';

export interface LinkProps {
  'aria-busy'?: boolean;
  'aria-disabled'?: boolean;
  'aria-label'?: string;
  children?: React.ReactNode;
  className?: string;
  /** Prevents activation by mouse and keyboard */
  disabled?: boolean;
  draggable?: 'false' | 'true';
  onClick?: (
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
  prefetch?: boolean | null;
  rel?: 'noreferrer';
  replace?: boolean;
  scroll?: boolean;
  tabIndex?: number;
  target?: HTMLAttributeAnchorTarget;
  title?: string;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  url?: string;
}

const Link = forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkProps>(
  (
    {
      children,
      disabled = false,
      draggable,
      onClick,
      prefetch = false,
      replace,
      scroll,
      target,
      type,
      url,
      ...rest
    },
    ref,
  ) => {
    const hasAnchor = url?.includes('#');
    const isExternal = url?.startsWith('http') ? true : false;
    const { setActiveModal } = usePageDetailsContext();
    const queryParams = new URLSearchParams(url?.split('?')[1]);
    const modal = queryParams.get('modal');

    const handleClickModal = () => {
      if (!modal || disabled) return;

      setActiveModal(modal as Content.Components['ModalType']);
    };

    // Anchors have no native disabled state, so a disabled one is rendered without
    // an href: then neither a click, middle click nor "open in new tab" can navigate
    if (disabled && url && !modal) {
      return (
        // Stays focusable like other disabled controls, an anchor without an href isn't by default
        <a
          tabIndex={0}
          {...rest}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          aria-disabled="true"
          role="link"
        >
          {children}
        </a>
      );
    }

    if (modal) {
      return (
        // aria-disabled instead of the native attribute, so it stays focusable like a disabled Button
        <button
          {...rest}
          ref={ref as React.ForwardedRef<HTMLButtonElement>}
          aria-disabled={disabled || rest['aria-disabled'] || undefined}
          onClick={handleClickModal}
          type={type}
        >
          {children}
        </button>
      );
    }

    if (!url) {
      if (!onClick) {
        return (
          <span {...rest} ref={ref as React.ForwardedRef<HTMLSpanElement>}>
            {children}
          </span>
        );
      } else {
        return (
          <button
            {...rest}
            ref={ref as React.ForwardedRef<HTMLButtonElement>}
            disabled={disabled}
            onClick={onClick}
            type={type}
          >
            {children}
          </button>
        );
      }
    }

    if (hasAnchor || isExternal || target === '_blank') {
      return (
        <a
          {...rest}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          draggable={draggable}
          href={url}
          onClick={onClick}
          // HubSpot's tracking script adds __hstc params to links after hydration
          // so we need to suppress the hydration warning for links that have them
          suppressHydrationWarning
          target={target}
        >
          {children}
        </a>
      );
    }

    return (
      <NextLink
        {...rest}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        href={url}
        onClick={onClick}
        prefetch={prefetch}
        replace={replace}
        scroll={scroll}
      >
        {children}
      </NextLink>
    );
  },
);

Link.displayName = 'Link';

export default Link;
